import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { createSelector } from 'reselect';
import moment from 'moment';

import { Urls, ModalTypes, ServiceTypes, ReportTypes } from 'constants/constants';
import { formatHours as fm, formatNumber as fn, formatPercent as fp, formatHours } from 'utils/utils';
import { usePolling } from 'utils/components';

import { selectCurrentAccount, getChannelSettings, getChannelTags } from 'store/models/Account';
import Channel, { selectChannel, twitchUrl, youtubeUrl } from 'store/models/Channel';
import Campaign, { selectCampaigns } from 'store/models/Campaign';
import Stats, { selectStatsByChannel, selectChannelStreamStats, selectChannelLinkStats, selectChannelSceneStats, selectChannelGameStats } from 'store/models/Stats';
import Overlay, { selectDefaultOverlay } from 'store/models/Overlay';
import Session, { selectSessionsForChannel } from 'store/models/Session';
import Stream, { selectStreamsForChannel, isStreamLive } from 'store/models/Stream';
import Game, { selectAllGames } from 'store/models/Game';
import Link, { selectLinksForChannel } from 'store/models/Link';
import Scene, { selectScenesForChannel } from 'store/models/Scene';
import AppState, { selectDates } from 'store/AppState';
import RouteState, { newTab, currentPath } from 'store/RouteState';
import UIState from 'store/UIState';

import { ContentWide, MegaHeader, Header, Section, SectionTitle } from 'components/Content';
import ScrollToTop from 'components/ScrollToTop';
import ChannelCard from 'components/ChannelCard';
import { StatBox, StatsBoxList } from 'components/Stats';
import DateRange from 'components/DateRange';
import { SceneStatList } from 'components/SceneItem';
import { Button, DeleteButton, RefreshIconButton, DownloadIconButton, ButtonList } from 'components/Button';
import { StreamStatList } from 'components/StreamItem';
import { GameList } from 'components/GameItem';
import { LinkList } from 'components/LinkItem';
import { EmptyItem } from 'components/EmptyItem';
import * as Icons from 'components/Svg';
import { TagList, ChannelGroupTag } from 'components/Tag';
import { LoadingText } from 'components/Loading';




// ======================
// STORE
// ======================
const selectors = createSelector(
  (_, props) => props.match.params.channelId,
  selectCurrentAccount,
  (state, props) => selectChannel(state, props.match.params.channelId),
  (state, props) => selectStreamsForChannel(state, props.match.params.channelId),
  (state, props) => selectSessionsForChannel(state, props.match.params.channelId),
  (state, props) => selectScenesForChannel(state, props.match.params.channelId),
  (state, props) => selectLinksForChannel(state, props.match.params.channelId),
  (state, props) => selectStatsByChannel(state, props.match.params.channelId),
  (state, props) => selectAllGames(state),
  s => selectCampaigns(s),
  selectChannelStreamStats,
  selectChannelGameStats,
  selectChannelSceneStats,
  selectChannelLinkStats,
  selectDefaultOverlay,
  selectDates,
  (channelId, account, channel, streams, sessions, scenes, links, stats, games, campaigns, streamStats, gameStats, sceneStats, linkStats, overlay, dates,) => {
    let activeScenes = campaigns |> _.flatMap(#, 'scenes') |> _.map(#, 'sceneId') |> _.uniq;
    let activeLinks = campaigns |> _.flatMap(#, 'links') |> _.map(#, 'linkId') |> _.uniq;
    let settings = getChannelSettings(account, channelId);
    streams = _.orderBy(streams, 'created', 'desc');
    sessions = _.keyBy(sessions, 'streamId');
    let latestStream = _.first(streams);
    let latestSession = sessions[latestStream?._id];
    let isLive = isStreamLive(latestStream);
    let hasSlideshow = !_.isEmpty(latestSession);
    games = games 
      |> _.pick(#, _.keys(gameStats)) 
      |> _.orderBy(#, g => gameStats[g._id]?.gameTimeMinutes ?? 0, 'desc');

    
    return { 
      account,
      channelId,
      channel,
      settings,
      streams,
      games,
      sessions,
      scenes: _.orderBy(scenes, s => {
        let stat = sceneStats?.[s._id]?.sceneViews ?? 0;
        return _.includes(activeScenes, s._id) ? stat + 100000000000000 : stat
      }, 'desc'),
      links: _.orderBy(links, l => {
        let stat = linkStats?.[l._id]?.clicks ?? 0;
        return _.includes(activeLinks, l._id) ? stat + 100000000000000 : stat
      }, 'desc'),
      stats,
      streamStats,
      gameStats,
      sceneStats,
      linkStats,
      overlay,
      activeLinks,
      activeScenes,
      isLive,
      hasSlideshow,
      dates,
    }
  },
);


// ======================
// COMPONENT
// ====================== 
const ChannelDetailsScreen = ({match}) => {
  const { account, channelId, channel, settings, streams, sessions, scenes, links, games, stats, streamStats, sceneStats,
    linkStats, gameStats, overlay, activeLinks, activeScenes, isLive, hasSlideshow, dates,  } = useSelector(s => selectors(s, { match }));
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState({
    channel: false,
    channelStats: false,
    sceneStats: false,
    linkStats: false,
    streamStats: false,
    gameStats: false,
  });

  const serviceName = channel?.service === ServiceTypes.TWITCH ? 'Twitch' : 'YouTube';
  const serviceIcon = channel?.service === ServiceTypes.TWITCH ? <Icons.TwitchIcon /> : <Icons.YouTubeIcon />;
  const tags = getChannelTags(account, channel?._id);
  const hasTags = !_.isEmpty(tags);

  const fetchData = async () => {
    fetchStats(dates);

    setLoading(true);
    await dispatch(Channel.actions.fetchChannels());
    await dispatch(Campaign.actions.fetchCampaigns());
    await dispatch(Overlay.actions.fetchOverlay());
    setLoading(false);
  }

  const fetchStats = async (forDates) => {
    let from = forDates.from.valueOf();
    let to = forDates.to.valueOf();

    let isLoading = _.mapValues(loadingStats, () => true);
    setLoadingStats(isLoading);

    await dispatch(Campaign.actions.fetchCampaigns());

    await dispatch(Stats.actions.fetchChannelDetailStats(channelId, from, to));
    setLoadingStats({..._.set(isLoading, 'channelStats', false)});

    await dispatch(Stats.actions.fetchChannelSceneStats(channelId, from, to));
    setLoadingStats({..._.set(isLoading, 'sceneStats', false)});

    await dispatch(Stats.actions.fetchChannelLinkStats(channelId, from, to));
    setLoadingStats({..._.set(isLoading, 'linkStats', false)});

    await dispatch(Stats.actions.fetchChannelStreamStats(channelId, from, to));
    setLoadingStats({..._.set(isLoading, 'streamStats', false)});

    await dispatch(Stats.actions.fetchChannelGameStats(channelId, from, to));
    setLoadingStats({..._.set(isLoading, 'gameStats', false)});
  };

  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, []);

  const onDateChange = async (_, newDates) => fetchStats(newDates);
  const onRefreshStats = async () => fetchData();
  const onSettings = () => dispatch(RouteState.actions.navigate(Urls.CHANNEL_SETTINGS_ROUTE, { channelId }));
  const onSlideshow = () => dispatch(RouteState.actions.navigate(Urls.SLIDESHOW_EDIT_ROUTE, { channelId }, null, { back: currentPath() }));
  const onSessionCheck = () => dispatch(UIState.actions.showModal(ModalTypes.HEALTH_CHECK, { overlayId: overlay?._id, channelId }));
  const onChannelClick = () => channel?.service === ServiceTypes.TWITCH
    ? newTab(twitchUrl(channel))
    : newTab(youtubeUrl(channel));
  const onExportView = () => dispatch(UIState.actions.showModal(ModalTypes.EXPORT, { report: ReportTypes.CHANNEL, channelId }));
  const onStreamClick = ({ _id: streamId }) => dispatch(RouteState.actions.navigate(Urls.STREAM_DETAILS_ROUTE, { streamId }))

  return (<>
    <ScrollToTop />
    <MegaHeader className="channel-details">
      <div>
        <ChannelCard channel={channel} size="xxl" showLogo="name">
        <div className="status-list">
          { isLive
            ? <div className="on live"><Icons.SignalIcon size="s" color="green" /> LIVE</div>
            : <div><Icons.SignalIcon size="s" /> OFFLINE</div>}
          { settings?.enableChatbot
            ? <div className="on"><Icons.ChatIcon size="s" color="green" /> Chat Bot Running</div>
            : <div><Icons.ChatIcon size="s" /> Chat Bot Offline</div>}
          { hasSlideshow
            ? <div className="on"><Icons.SlideshowIcon size="s" color="green" /> Slideshow Running</div>
            : <div><Icons.SlideshowIcon size="s" /> Slideshow Offline</div>}
        </div>  
        </ChannelCard>
        { hasTags &&
        <TagList className="channel-tags" size="s">
          { _.map(tags, tag => <ChannelGroupTag key={tag.tagId} tag={tag} />)}
        </TagList>}
      </div>
      <ButtonList direction="column" align="stretch">
          <Button color="light" onClick={onSettings}><Icons.SettingsIcon /> Settings & Urls</Button>
          <Button color="light" onClick={onSlideshow}><Icons.SlideshowIcon /> Slideshow Editor</Button>
          <Button color="light" onClick={onChannelClick}>{serviceIcon} {serviceName} Channel</Button>
          <Button color="light" onClick={onSessionCheck}><Icons.HealthIcon /> Health Check</Button>
      </ButtonList>
    </MegaHeader>
    
    <ContentWide className="channel-details">
      
      <Section className="date-range-header">
        <div className="total-value">
          <div className="label">Total Media Value</div>
          <div className="value">
            { loading 
              ? <LoadingText />
              : `$${fn(stats?.totalMediaValue)}`}
          </div>
        </div>
        <div className="date-export">
          <DateRange disabled={loading} onDateChange={onDateChange} />
          <DownloadIconButton color="light" disabled={loading} onClick={onExportView} />
          <RefreshIconButton color="light" loading={loading} onClick={onRefreshStats} />
        </div>
      </Section>
      
      <Section>
        <SectionTitle>Slides</SectionTitle>
        <StatsBoxList rowCount={4} wrap={true}>
          <StatBox
            label="Slideshow Value"
            value={`$${fn(stats?.sceneMediaValue)}`}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Impressions"
            value={fn(stats?.sceneViews)}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Fill Rate"
            value={fp(stats?.fillRate)}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Unique Slides"
            value={fn(stats?.sceneIds)}
            loading={loadingStats.channelStats} />
        </StatsBoxList>
        <SceneStatList scenes={scenes} sceneStats={sceneStats} activeScenes={activeScenes} loading={loadingStats.sceneStats} loadingCount={2} />
      </Section>

      { channel?.service !== ServiceTypes.YOUTUBE && 
      <Section size="l">
        <SectionTitle>Links & Chat</SectionTitle>
        <StatsBoxList rowCount={4} wrap={true}>
          <StatBox
            label="Click Value"
            value={`$${fn(stats?.clicksMediaValue)}`}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Clicks"
            value={fn(stats?.clicks)}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Link Views"
            value={fn(stats?.messageViews)}
            loading={loadingStats.channelStats} />
          <StatBox
            label="CTR"
            value={fp(stats?.linkCtr, 1)}
            loading={loadingStats.channelStats} />
        </StatsBoxList>
        <LinkList links={links} linkStats={linkStats} activeLinks={activeLinks} loading={loadingStats.linkStats} loadingCount={2} />
      </Section>}

      <Section size="l">
        <SectionTitle>Streams</SectionTitle>
        <StatsBoxList rowCount={4} wrap={true}>
          <StatBox
            label="Peak Viewers"
            value={fn(stats?.streamMaxViewers)}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Avg. Viewers"
            value={fn(stats?.streamAvgViewers)}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Streams"
            value={`${fn(stats?.streamCount)}`}
            loading={loadingStats.channelStats} />
          <StatBox
            label="Time Streaming"
            value={fm(stats?.streamMinutes)}
            loading={loadingStats.channelStats} />
        </StatsBoxList>
        <StreamStatList channels={{[channel?._id]: channel}} streams={streams} stats={streamStats} sessions={sessions} loading={loadingStats.streamStats} onStreamClick={onStreamClick} />
      </Section>

      <Section size="l">
        <SectionTitle>Games</SectionTitle>
        <GameList games={games} stats={gameStats} loading={loadingStats.gameStats} showName={false} loadingCount={2} />
      </Section>
    </ContentWide>
  </>)
}
export default ChannelDetailsScreen;
